import { Component, Directive, Input, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { NavService } from 'src/app/core/services/nav.service';
import { ShoppingCartService } from 'src/app/core/services/shoppingCart.service';
import { ViewerModalComponent } from 'src/app/shared/components/image-viewer/viewer-modal.component';

import { MenuProduct } from 'src/app/shared/model/MenuProduct';
import { Extra } from 'src/app/shared/model/Extra';
import { Option } from 'src/app/shared/model/Option';
import { ComboDTO } from 'src/app/shared/model/dto/ComboDTO';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/core/services/utils.service';
import { KeenTrackingService } from 'src/app/core/services/keenTracking.service';

import { TranslatePipe } from '@ngx-translate/core';
import { RestaurantService } from 'src/app/core';
import { OrderFlowTraceService } from 'src/app/core/services/telemetry/order-flow-trace.service';

// Base compartilhada (@Directive) - ver a nota em pizza-nep.page.ts.
@Directive()
export class ExtraPageBase implements OnInit, AfterViewInit, OnDestroy {

	combo: ComboDTO;
	@Input() extras: Extra[] = [];
	@Input() item: MenuProduct;
	@ViewChild('complementsBody') complementsBodyRef: ElementRef;
	@ViewChild('stickyHeader') stickyHeaderRef: ElementRef;
	@ViewChild('noImageHeader') noImageHeaderRef?: ElementRef;
	@ViewChild('imageWrap', { read: ElementRef }) imageWrapRef?: ElementRef;

	itemTemplate: MenuProduct;
	imgURL = environment.imgURL;

	public isNewComplementsPedirDelivery = false;

	private stickyFadeListener: () => void;
	public allowNotes = true;
	public observation = '';
	public itemQty = 1;
	public selectedExtras: Option[] = [];
	public minQtyIncrementByFive: number;
	public minQtyIncrementByTen: number;

	constructor(
		private navService: NavService,
		private cartService: ShoppingCartService,
		private modalController: ModalController,
		private utilsService: UtilsService,
		private translatePipe: TranslatePipe,
		private keen: KeenTrackingService,
		private rService: RestaurantService,
		private utils: UtilsService,
        private sanitizer: DomSanitizer,
		private orderFlowTrace: OrderFlowTraceService
	) { }

	ngOnInit() {
		this.loadExtrasData();
	}

	ionViewWillEnter() {
		this.loadExtrasData();
	}

	/**
	 * Monta a tela de complementos a partir do que o `NavService` carrega.
	 *
	 * ## Os dois defeitos que este método tinha, e por que eram invisíveis
	 *
	 * 1. **`pop()` sem `return`.** `navService.get('extras')` devolve `false`
	 *    quando a chave não existe (é o contrato do `get`, não `undefined`), e o
	 *    `if (!this.extras) this.navService.pop()` mandava voltar mas **deixava o
	 *    método seguir**. Doze linhas abaixo, `this.extras.filter(...)` rodava
	 *    sobre `false` → `TypeError: this.extras.filter is not a function`.
	 *    Era o TypeError MAIS FREQUENTE das builds corrigidas em 15/08/2026
	 *    (8 devices), diagnosticado e nunca consertado.
	 *
	 *    `pop()` é assíncrono: ele agenda a navegação e devolve na hora. Um
	 *    `if` sem `return` sobre navegação é sempre bug — o código continua
	 *    rodando num estado que já foi declarado inválido.
	 *
	 * 2. **`restaurant.info` sem guarda.** Mesmo padrão do MP-569: o
	 *    `restaurant` pode não ter carregado, e a linha lançava antes de a tela
	 *    existir. As linhas vizinhas já usavam `?.` — esta ficou para trás.
	 *
	 * Ambos travam a tela inteira, porque um throw no `ngOnInit` aborta o
	 * componente e o usuário fica olhando o que estava antes.
	 */
	private loadExtrasData() {
		this.extras = this.navService.get('extras');

		// `return` obrigatório: `pop()` só AGENDA a saída. Sem ele o método
		// segue e explode em `this.extras.filter` doze linhas abaixo.
		if (!Array.isArray(this.extras)) {
			this.navService.pop();
			return;
		}

		this.item = this.navService.get('item');
		if(this.navService.get('combo')) this.combo = this.navService.get('combo');

		this.isNewComplementsPedirDelivery = !!(this.rService.restaurant?.info?.modules?.new_complements_pedir_delivery);
		this.allowNotes = this.rService.restaurant?.info?.allow_order_comments;
		this.minQtyIncrementByFive = 50;
		this.minQtyIncrementByTen = 100;
		const orderLevelExtrasEnabled = !!this.rService.restaurant?.info?.modules?.order_level_extras;
		if (orderLevelExtrasEnabled) {
			this.extras = this.extras.filter(extra => extra.options.length > 0 && !extra.isOrderLevel).map(itemExtra => {
				itemExtra.qty = 0;
				itemExtra.options.forEach(option => option.qty = 0);
				itemExtra.radioGroup = 0;
				return itemExtra;
			});
		} else {
			this.extras = this.extras.filter(extra => extra.options.length > 0).map(itemExtra => {
				itemExtra.qty = 0;
				itemExtra.options.forEach(option => option.qty = 0);
				itemExtra.radioGroup = 0;
				return itemExtra;
			});
		}
	}

	ngAfterViewInit() {
		if (!this.isNewComplementsPedirDelivery) return;
		setTimeout(() => {
			const body = this.complementsBodyRef?.nativeElement as HTMLElement;
			if (!body) return;
			const stickyHeader = this.stickyHeaderRef?.nativeElement as HTMLElement;
			const complementsEl = body.parentElement;
			const triggerEl = (this.noImageHeaderRef ?? this.imageWrapRef)?.nativeElement as HTMLElement | undefined;
			this.stickyFadeListener = () => {
				const bodyTop = body.getBoundingClientRect().top;
				if (triggerEl && stickyHeader) {
					const shouldShow = triggerEl.getBoundingClientRect().bottom <= bodyTop;
					stickyHeader.classList.toggle('complements__sticky-header--visible', shouldShow);
					complementsEl?.classList.toggle('complements--sticky-active', shouldShow);
				}
				body.querySelectorAll<HTMLElement>('.complements__group').forEach(group => {
					const header = group.querySelector<HTMLElement>('.complements__group-header');
					if (!header) return;
					const remaining = group.getBoundingClientRect().bottom - bodyTop - header.offsetHeight;
					header.style.opacity = String(Math.min(1, Math.max(0, remaining / 56)));
				});
			};
			body.addEventListener('scroll', this.stickyFadeListener, { passive: true });
		}, 150);
	}

	ngOnDestroy() {
		const body = this.complementsBodyRef?.nativeElement as HTMLElement;
		if (body && this.stickyFadeListener) {
			body.removeEventListener('scroll', this.stickyFadeListener);
		}
	}

	onImgError(item) {
		item.image = '';
	}

	checkboxBeyondLimit(index) {
		return this.extras[index].options.filter(item => item.isChecked).length >= this.extras[index].qty_max;
	}

	scrollToCategory(extraElementId, smooth = false) {
		const categoryElement = document.getElementById(extraElementId);
		if (!categoryElement) return;

		if (!this.isNewComplementsPedirDelivery) {
			categoryElement.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
			return;
		}

		const body = this.complementsBodyRef?.nativeElement as HTMLElement;
		if (!body) return;

		const stickyOffset = this.stickyHeaderRef?.nativeElement?.offsetHeight ?? 0;
		body.scrollTo({
			top: body.scrollTop + categoryElement.getBoundingClientRect().top - body.getBoundingClientRect().top - stickyOffset,
			behavior: smooth ? 'smooth' : 'auto',
		});
	}

	jumpToNextExtra(extraIndex) {
		let nextExtraID = this.extras[extraIndex + 1].id;
		const prefix = this.isNewComplementsPedirDelivery ? 'extra-v2-' : 'extra-';
		setTimeout(() => {
			this.scrollToCategory(prefix + nextExtraID, true);
		}, 50);
	}

	changeInputQuantity(extra, optionSelected) {

		let inputValue = optionSelected.qty?.toString();

		optionSelected.qty = this.parseQuantity(inputValue);

		let totalExtra = extra.options.reduce((total,option) => {
			if(option.id !== optionSelected.id)
				return total += option.qty;
			return total;
		}, 0)

		if (optionSelected.qty > extra.qty_max || totalExtra >= extra.qty_max || (totalExtra + optionSelected.qty > extra.qty_max) || (extra.qtyPerItem && (optionSelected.qty > extra.qtyPerItem)))
			this.utilsService.createToaster(this.translatePipe.transform("Maximum amount reached"));

		extra.qty = (totalExtra + optionSelected.qty);
	}

	parseQuantity(inputValue) {
		if (!inputValue)
			return null

		inputValue = inputValue.replace(/[^0-9,.]/g, "");

		if (inputValue.includes(',') || inputValue.includes('.'))
			inputValue = inputValue.split(/[,\.]/)[0][0];

		const parsedValue = parseInt(inputValue, 10);

		if (isNaN(parsedValue) || parsedValue < 0)
			return null

		return parsedValue;
	}


	/**
	 * OP-1110 — complemento sem estoque não entra no carrinho.
	 *
	 * A checagem mora aqui, e não no template, porque o clique está na LINHA inteira
	 * (`firstIncrement` no layout antigo, `firstIncrementNew` no novo) e é ela quem
	 * seleciona. No layout antigo a linha é um `<ion-item>`, que não aceita `disabled`;
	 * o `[disabled]` que existia cobria só o radio e o input de quantidade. O layout
	 * novo não quebrava por acidente: um `pointer-events: none` no CSS barrava o
	 * clique — regra de negócio a um ajuste de estilo de distância de voltar.
	 *
	 * Compara com `=== false` de propósito: `hasStock` é opcional e `undefined`
	 * significa "sem controle de estoque". `!option.hasStock` bloquearia esses também.
	 */
	private isOutOfStock(option): boolean {
		return option?.hasStock === false;
	}

	firstIncrement(extraIndex, optionIndex) {
		let extra = this.extras[extraIndex];
		let selectedOption = extra.options[optionIndex];
		let isUniqueExtra = extra.type == 1;
		let isQttyExtra = extra.type == 2;
		let isMultipleExtra = extra.type == 3;

		if (selectedOption.asTitle) return;

		if (this.isOutOfStock(selectedOption)) return;

		if (isUniqueExtra) {
			extra.radioGroup = selectedOption.id;
			extra.options.forEach(option => {
				option.qty = 0
			})
			selectedOption.qty = 1;

		} else if (isMultipleExtra && (extra.qtyPerItem == 1 || !extra.qtyPerItem)) {
			selectedOption.isChecked = !selectedOption.isChecked;
			selectedOption.qty = selectedOption.isChecked ? 1 : 0;
			extra.qty = extra.options.reduce((total, option) => total + (option.qty || 0), 0);
		} else if (selectedOption.qty == 0) {
			this.incrementQty(extraIndex, optionIndex);
		}

		let hasNextExtra = this.extras.length - 1 > extraIndex;
		if (hasNextExtra) {
			let extraIsFull = false;

			if (isUniqueExtra) extraIsFull = extra.radioGroup != 0;
			else if (isQttyExtra) extraIsFull = extra.qty == extra.qty_max;

			if (extraIsFull) {
				this.jumpToNextExtra(extraIndex);
			}
		}
	}

	firstIncrementNew(extraIndex, optionIndex) {
		const extra = this.extras[extraIndex];
		const selectedOption = extra.options[optionIndex];
		const isMultipleToggle = extra.type == 3 && (extra.qtyPerItem == 1 || !extra.qtyPerItem);

		if (isMultipleToggle && !selectedOption.isChecked && extra.qty >= extra.qty_max)
			return this.utilsService.createToaster(this.translatePipe.transform("Maximum amount reached"));

		this.firstIncrement(extraIndex, optionIndex);
	}

	incrementQty(extraIndex, optionIndex) {
		let extra = this.extras[extraIndex];

		// Segundo caminho de escrita: no layout antigo o "+" é um <ion-icon>, que não
		// aceita `disabled` — sem esta guarda ele incrementava o complemento sem estoque
		// mesmo com a linha bloqueada.
		if (this.isOutOfStock(extra.options[optionIndex])) return;

		if (extra.qty >= extra.qty_max || extra.qtyPerItem && extra.options[optionIndex].qty >= extra.qtyPerItem)
			return this.utilsService.createToaster(this.translatePipe.transform("Maximum amount reached"));
		let qty = 1;
		if (extra.qty_min >= this.minQtyIncrementByFive) qty = 5;
		if (extra.qty_min >= this.minQtyIncrementByTen) qty = 10;
		if (extra.qty + qty > extra.qty_max) qty = extra.qty_max - extra.qty;
		if (extra.qtyPerItem && extra.options[optionIndex].qty + qty > extra.qtyPerItem) qty = extra.qtyPerItem - extra.qty;
		extra.qty += qty;
		extra.options[optionIndex].qty += qty;
	}

	convertToSafeHtml(rawHtml: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(rawHtml);
    }

	isInstagram() {
		var ua = navigator.userAgent;
		return (ua.indexOf('Instagram') > -1) ? true : false;
	}

	removeHtmlTags(str: string) {
		return this.utils.removeTags(str);
	}

    getSafeHtml(value: any) {
		const sanitizedValue = this.utils.sanitizeAndFormatText(value);
		return this.removeHtmlTags(sanitizedValue);

		// try {
        //     return this.convertToSafeHtml(JSON.parse(value));
        // } catch (error) {
        //     return this.removeHtmlTags(value);
        // }
    }

	decrementQty(extraIndex, optionIndex, event) {
		event.stopPropagation();
		let extra = this.extras[extraIndex];
		if (extra.options[optionIndex].qty == null || extra.options[optionIndex].qty == 0) return
		let qty = 1;
		extra.qty -= qty;
		extra.options[optionIndex].qty -= qty;
	}

	getSelectedExtras() {
		this.selectedExtras = [];

		this.extras.forEach(extra => {
			extra.options.forEach(option => {
				let price = 0;
				if (extra.type == 1 && option.id == extra.radioGroup) {
					price = option.price
					option.total = price;
					option = JSON.parse(JSON.stringify(option));
					this.selectedExtras.push(option);
				}
				else if ((extra.type == 2 || extra.type == 3) && option.qty > 0) {
					price = (option.qty * option.price);
					option.total = price;
					option = JSON.parse(JSON.stringify(option));
					this.selectedExtras.push(option);
				}
			})
		})

		return this.selectedExtras;
	}

	calculateTotalPrice() {
		let totalPrice = this.item.price ? this.item.price : 0;

		totalPrice += this.selectedExtras.reduce((extraPrice, extra) => {
			return extraPrice += extra.total;
		}, 0);

		return totalPrice;
	}

	validateMinimumAndMaximumQuantity(extra){
		let qty = extra.options.reduce((total, option) => total += option.qty, 0);
		return qty >= extra.qty_min && qty <= extra.qty_max;
	}

	isDisabled() {

		let disabled = this.extras.some(extra => {
			let valid = false;
			if (extra.type == 1) {
				valid = extra.required ? extra.options.some(option => option.id == extra.radioGroup) : true
			} else 	if ((extra.type == 2 || extra.type == 3)) {
				valid = this.validateQuantityForExtraItem(extra);
			} else {
				let qty = extra.options.filter(option => option.isChecked).length;
				valid = qty >= extra.qty_min && qty <= extra.qty_max;
			}

			return !valid;
		})

		return disabled;
	}

	isGroupSatisfied(extra: Extra): boolean {
		if (extra.type === 1) {
			return !extra.required || (extra.radioGroup != null && extra.radioGroup !== 0);
		}
		const qty = extra.qty || 0;
		const min = extra.qty_min || 0;
		return qty >= min;
	}

	validateQuantityForExtraItem(extra){
		if (!extra.qtyPerItem) {
			return this.validateMinimumAndMaximumQuantity(extra);
		}

		const validQuantityPerItem = extra.options.every(option => option.qty <= extra.qtyPerItem);
		return validQuantityPerItem && this.validateMinimumAndMaximumQuantity(extra);
	}

	incrementItemQty() {
		this.itemQty++;
	}

	decrementItemQty() {
		if (this.itemQty > 1) this.itemQty--;
	}

	addToShoppingCart() {
		this.orderFlowTrace.emit('ClientProductPriceCalculated', {
			in: {
				base_price: this.item?.price,
				selected_extras: (this.selectedExtras || []).map(e => ({ id: e.id, qty: e.qty, price: e.price })),
			},
			out: { total: this.calculateTotalPrice() },
		}, 'extra.addToShoppingCart');
		if (this.isNewComplementsPedirDelivery) {
			return this.addToShoppingCartNew();
		}

		if(this.combo) {
            this.combo.extras = this.selectedExtras;
            this.combo.totalPrice += this.calculateTotalPrice();
			this.cartService.addProductToCart(this.combo);
		}else {
			this.item.extras = this.selectedExtras;
			this.item.qty = 1;
			this.cartService.addProductToCart(this.item);
		}

		this.goToShoppingCart();
	}

	private addToShoppingCartNew() {
		if (this.combo) {
			this.combo.extras = this.selectedExtras;
			this.combo.totalPrice += this.calculateTotalPrice();
			this.cartService.addProductToCart(this.combo);
			this.goToShoppingCart();
			return;
		}

		this.item.extras = this.selectedExtras;
		this.item.qty = this.itemQty;
		(this.item as any).notes = this.observation;
		this.cartService.addProductToCart(this.item);
		this.goToShoppingCart();
	}

	async goToShoppingCart() {
		this.navService.push('shopping-cart');
	}

	async openViewer(item) {

		const modal = await this.modalController.create({
			component: ViewerModalComponent,
			componentProps: {
				src: this.imgURL+'/products/'+item.image+'/lg_'+item.image+'.jpg',
				scheme: 'dark',
				swipeToClose: false,
				title: item.name,
				text: item.description ? this.removeHtmlTags(item.description) : ''
			},
			cssClass: 'ion-img-viewer',
			keyboardClose: true,
			showBackdrop: true
		});
	
		await modal.present();
	  
		const topModal = await this.modalController.getTop();
		const element = topModal.querySelector('.ion-img-viewer ion-text');
	  
		if (element) {
		  const ionText = element as HTMLElement;
		  ionText.setAttribute('tabindex', '0');
		  ionText.focus();
		}
	  
		return modal;
	}

	back() {
		this.keen.event('navigateBackward', {
			'from': 'pizza',
			'to': 'menu'
		});

		this.navService.navigateBackward("menu");
	}

	onBlockedAction() {
		const firstUnsatisfied = this.extras.find(extra => !this.isGroupSatisfied(extra));
		if (!firstUnsatisfied) return;

		const prefix = this.isNewComplementsPedirDelivery ? 'extra-v2-' : 'extra-';
		this.scrollToCategory(prefix + firstUnsatisfied.id, true);
		this.utilsService.createToaster(
			this.translatePipe.transform('Please fill in the required fields')
		);
	}

	showStockPausedItems() {
		// `?.` aqui pelo mesmo motivo do `loadExtrasData`: o restaurante pode não
		// ter carregado, e um throw aqui aborta o render da tela inteira. É o
		// segundo dos dois acessos desprotegidos que sobraram neste arquivo.
		const info = this.rService.restaurant?.info;
		if (info?.modules?.ingredient_inventory) {
			return info.supply_stock_config?.display_paused_items_as_sold_out ?? false;
		}
		return info.stock_configuration?.display_paused_items_as_sold_out ?? false;
	}
}

@Component({
	selector: 'app-extra',
	templateUrl: './extra.page.html',
	styleUrls: ['./extra.page.scss'],
})
export class ExtraPage extends ExtraPageBase {}
